/**
 * AiMusicClassifier — SONICS SpecTTTra α-120s ONNX inference for /ai-detector.
 *
 * Public surface:
 *   await window.AiMusicClassifier.preload(slug, onProgress)
 *       → Promise<boolean>; EngineWarmer-compatible (true on success).
 *
 *   await window.AiMusicClassifier.classify(audioBuffer, {
 *       slug: 'ai-detector',
 *       signal: AbortSignal | null,
 *       onProgress: (windowIdx, totalWindows) => {},
 *   })
 *       → Promise<{
 *             pAi: number,          // 0..1 (sigmoid mean across windows)
 *             perWindow: number[],  // 0..1 per 120 s window — Phase D heatmap input
 *             windowsCount: number,
 *             durationCovered: number,  // seconds actually fed to model
 *             inferenceMs: number,
 *             modelVersion: string
 *         }>
 *
 * Pre-processing contract (must match the PyTorch export's expected input):
 *   • Resample to 16 000 Hz mono via OfflineAudioContext.
 *   • Window into 120 s chunks at 16 kHz = 1 920 000 samples each.
 *   • Tracks shorter than 120 s are zero-padded to 120 s.
 *   • Tracks longer than 120 s use 50 % overlap (60 s stride). We cap at
 *     N_WINDOWS_MAX so a 10-min track doesn't run 10 inferences. Mean-pool
 *     all windows. The per-window outputs feed the future heatmap UI.
 *
 * Why a separate module from StemSeparator: different ONNX model, different
 * input pipeline (stem-separator works on STFT magnitude tiles; we work on
 * raw waveform). Same ORT-Web loader is shared transparently — if
 * StemSeparator already loaded ORT, `loadOrt()` here is a no-op (the global
 * `window.ort` is reused). Same wasm path, same providers, same warm cost
 * across the user's session.
 *
 * Failure model — the page has no other verdict, so every failure must leave
 * the NEXT attempt possible:
 *   • ORT fails to load / a model source is down or silent → the attempt
 *     rejects and is forgotten; the next preload()/classify() starts over.
 *   • The loaded model computes wrong on this device (self-check) →
 *     isUnavailable() turns true: a retry on this page would reproduce it.
 *   • Inference throws mid-loop → classify rejects.
 *   • AbortSignal aborted → classify rejects with an AbortError; the model
 *     load itself is never cancelled (the next file needs the same model).
 *   • The load goes silent (a script tag, a GPU adapter request and a session
 *     create cannot be aborted) → the page's watchdog calls abandonLoad():
 *     the attempt is forgotten, loadPhase() said where it was, and if that
 *     was a GPU phase the next attempt does not ask for WebGPU.
 */
(function(){
'use strict';

// Pin ORT to the same version stem-separator uses. Bumping requires
// re-validating SONICS inference outputs against the PyTorch reference.
var ORT_PRIMARY  = '/vendor/onnxruntime/ort.min.js';
var ORT_FALLBACK = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.24.3/dist/ort.min.js';
var ORT_WASM_BASE = '/vendor/onnxruntime/';

var TARGET_SAMPLE_RATE = 16000;
var WINDOW_DURATION_S  = 120;
var WINDOW_SAMPLES     = TARGET_SAMPLE_RATE * WINDOW_DURATION_S;     // 1 920 000
var WINDOW_STRIDE_S    = 60;                                          // 50 % overlap
var WINDOW_STRIDE_SAMP = TARGET_SAMPLE_RATE * WINDOW_STRIDE_S;
var N_WINDOWS_MAX      = 6;   // 6 × 120 s with 50 % overlap covers up to ~7 min
var MODEL_VERSION      = 'sonics-spectttra-alpha-120s-mg2';   // -mg2 = head refit on 15 generators, 2026-09-30 (-mg1: 2026-07-10)

// Engine state — shared across all classify() calls in a page lifetime.
var ortLoadPromise = null;
var modelLoadPromise = null;
var onnxSession = null;
var modelInputName = null;     // discovered after session create
var modelOutputName = null;
var classifierAvailable = null;  // null = unknown, true = ready, false = the loaded model failed its self-check on this device
/* The load in flight, as its waiters see it. Measured 16.09–06.10: 16 runs sat
 * at «model_preload» for 180–260 s without one sign of life and were failed by
 * the page's watchdog — on desktops with 16–32 GB as often as on phones — and
 * nothing recorded WHERE. Only the download used to report progress; the ORT
 * script, the GPU adapter request, the session create and the three self-check
 * inferences after it were silent, and only the FIRST caller's callback was
 * kept, so a second file waiting on the same load heard nothing at all. */
var loadListeners = [];          // progress callbacks of every caller waiting for the load in flight
var loadPhase = '';              // ort | fetch | adapter | create | sanity
var loadRatio = 0;               // the last download ratio told to the listeners
var loadAbort = null;            // AbortController of the load in flight: abandonLoad() stops its download
var loadGen = 0;                 // bumped by abandonLoad(): a forgotten attempt publishes nothing
var loadAskedWebGPU = false;     // the attempt in flight asked ORT for the WebGPU provider
var sessionOnGpu = false;        // the published session runs on WebGPU (a GPU failure mid-run may move it to wasm)
var avoidWebGPU = false;         // WebGPU hung during a load on this page — it is not asked for again
var ADAPTER_WAIT_MS = 8000;      // a GPU adapter request that has not answered by then is treated as «no WebGPU»
/* Test seam, shared with the page's watchdog hook: a stand cannot wait out the
 * real limits. The object does not exist in production. */
function limit(name, dflt){
    var h = window.__aidDeepWatchdog;
    return (h && typeof h[name] === 'number') ? h[name] : dflt;
}

function tellLoad(ratio){
    if (typeof ratio === 'number') loadRatio = ratio;
    var ls = loadListeners.slice();
    for (var i = 0; i < ls.length; i++) { try { ls[i](loadRatio); } catch(_){} }
}
/* A phase boundary is a sign of life: every waiter is told, with the ratio it
 * already knows, so a slow device is not mistaken for a dead one. */
function enterPhase(name){ loadPhase = name; tellLoad(); }
function visibleAfter(fn, ms){
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    var h = setTimeout(fn, ms);
    return function(){ clearTimeout(h); };
}
/* `promise`, or `fallback` if it has not settled within `ms` of visible time. */
function within(promise, ms, fallback){
    return new Promise(function(resolve, reject){
        var done = false;
        var stop = visibleAfter(function(){ if (!done) { done = true; resolve(fallback); } }, ms);
        Promise.resolve(promise).then(
            function(v){ if (!done) { done = true; stop(); resolve(v); } },
            function(e){ if (!done) { done = true; stop(); reject(e); } }
        );
    });
}

/* The sources of THE model the page names — and nothing else. A third,
 * hard-coded URL used to close this list: the legacy fp16 file with the stock
 * head, still on both origins. When the two real sources failed it loaded
 * silently, and the page judged its scores by thresholds measured for another
 * head (2 sessions in three weeks, `ml_model_fallback {tried:2, sizeMB:37}`;
 * one got «human, 0 %»). A different model is not a fallback. */
function modelUrls(){
    var urls = [];
    var pushIfNew = function(u){ if (u && urls.indexOf(u) === -1) urls.push(u); };
    var meta   = document.querySelector('meta[name="aid-ml-model-url"]');
    var metaFb = document.querySelector('meta[name="aid-ml-model-url-fallback"]');
    if (meta && meta.content)   pushIfNew(meta.content);
    if (metaFb && metaFb.content) pushIfNew(metaFb.content);
    return urls;
}

/* How long a model source may stay silent — no response, then no bytes —
 * before the attempt is dropped and the next source tried. Without it a hung
 * connection held the load promise forever: the page's stall watchdog failed
 * the run after three minutes, and every retry then waited on the same dead
 * promise (6 of 24 failed runs in a week were `stalled_no_progress` at
 * model_preload). Counted in VISIBLE time: a hidden tab's network is throttled
 * or frozen, which is not the source being down. */
var FETCH_SILENCE_MS = 30000;
function silenceWatch(ms, onSilent){
    var visNow = function(){ return (typeof window.__visibleNow === 'function') ? window.__visibleNow() : Date.now(); };
    var w = { fired: false, last: visNow(), cancel: null };
    function arm(wait){
        w.cancel = visibleAfter(function(){
            var quiet = visNow() - w.last;
            if (quiet + 5 >= ms) { w.fired = true; try { onSilent(); } catch(_){} }
            else arm(ms - quiet);
        }, wait);
    }
    arm(ms);
    w.beat = function(){ w.last = visNow(); };
    w.stop = function(){ if (w.cancel) { try { w.cancel(); } catch(_){} w.cancel = null; } };
    return w;
}

function ae(slug, action, payload){
    try {
        if (typeof window.__ae !== 'function') return;
        var p = { slug: slug || 'ai-detector', action: action };
        if (payload) for (var k in payload) if (payload.hasOwnProperty(k)) p[k] = payload[k];
        window.__ae('feature_use', p);
    } catch(_){}
}

function loadOrt(){
    if (window.ort) return Promise.resolve();
    if (ortLoadPromise) return ortLoadPromise;
    ortLoadPromise = new Promise(function(resolve, reject){
        function tryLoad(url, onFail){
            var s = document.createElement('script');
            s.src = url;
            s.onload = function(){
                try {
                    if (window.ort && ort.env && ort.env.wasm) {
                        // Absolute URL so ORT-Web's internal `import()` of
                        // jsep helpers resolves correctly even from contexts
                        // where the importing script's base URL would not
                        // honour origin-relative specifiers (blob workers,
                        // cached SW responses). Pattern from apollo-worker.js.
                        var _wp = ORT_WASM_BASE;
                        if (_wp.charAt(0) === '/' && typeof location !== 'undefined' && location.origin) _wp = location.origin + _wp;
                        ort.env.wasm.wasmPaths = _wp;
                        ort.env.wasm.numThreads = (typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated)
                            ? Math.min(navigator.hardwareConcurrency || 4, 4)
                            : 1;
                    }
                } catch(_){}
                resolve();
            };
            s.onerror = onFail;
            document.head.appendChild(s);
        }
        tryLoad(ORT_PRIMARY, function(){
            tryLoad(ORT_FALLBACK, function(){
                ortLoadPromise = null;
                reject(new Error('ORT load failed (both sources)'));
            });
        });
    });
    return ortLoadPromise;
}

/* The model comes through the SHARED resumable downloader (model-download.js →
 * self.__mdFetchBytes) when the page loaded it: a dropped connection resumes
 * with Range from the byte it reached instead of starting the 73 MB over, a
 * short stream is a break and not a model, and a truncated copy is cleaned
 * out of every cache. Measured on /ai-detector since 2026-08-01: 31 of the 52
 * failed classifier runs died on the download («network error», «Failed to
 * fetch», «Load failed», 30 s of silence, the stall watchdog in model_preload);
 * each retry, and each next address, started from zero. The single-pass read
 * below stays for a page that did not get the primitive.
 * `signal` belongs to the load: abandonLoad() stops the download it forgets,
 * so a retry does not race the abandoned one for the same connection. */
async function fetchModelBytes(slug, onProgress, signal){
    var urls = modelUrls();
    var lastErr = null;
    var md = (typeof self !== 'undefined' && typeof self.__mdFetchBytes === 'function') ? self.__mdFetchBytes : null;
    for (var i = 0; i < urls.length; i++) {
        var url = urls[i];
        if (signal && signal.aborted) throw abortedLoad();
        if (md) {
            try {
                var got = await md(url, {
                    signal: signal || undefined,
                    stallMs: limit('fetchSilenceMs', FETCH_SILENCE_MS),
                    onProgress: function(received, total){
                        if (!onProgress) return;
                        if (total) onProgress(received / total); else onProgress();
                    }
                });
                if (i > 0) ae(slug, 'ml_model_fallback', { tried: i, sizeMB: Math.round(got.length / 1048576) });
                return got;
            } catch (e) {
                if (signal && signal.aborted) throw abortedLoad();
                lastErr = e;
                continue;
            }
        }
        var loaded = 0;
        var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
        var relay = null;
        if (ctrl && signal) { relay = function(){ try { ctrl.abort(); } catch(_){} }; try { signal.addEventListener('abort', relay); } catch(_){} }
        var silenceMs = limit('fetchSilenceMs', FETCH_SILENCE_MS);
        var watch = silenceWatch(silenceMs, function(){ if (ctrl) try { ctrl.abort(); } catch(_){} });
        try {
            var response = await fetch(url, ctrl ? { credentials: 'omit', signal: ctrl.signal } : { credentials: 'omit' });
            if (!response.ok) throw new Error('HTTP ' + response.status);
            watch.beat();
            var total = parseInt(response.headers.get('Content-Length')) || 0;
            var reader = response.body.getReader();
            var chunks = [];
            while (true) {
                var read = await reader.read();
                if (read.done) break;
                watch.beat();
                chunks.push(read.value);
                loaded += read.value.length;
                /* A response without a length still tells its waiters it is alive. */
                if (onProgress) { if (total) onProgress(loaded / total); else onProgress(); }
            }
            var bytes = new Uint8Array(loaded);
            var off = 0;
            for (var c = 0; c < chunks.length; c++) { bytes.set(chunks[c], off); off += chunks[c].length; }
            if (total && loaded < total) throw new Error('truncated download: ' + loaded + '/' + total);
            if (i > 0) ae(slug, 'ml_model_fallback', { tried: i, sizeMB: Math.round(bytes.length / 1048576) });
            return bytes;
        } catch (e) {
            if (signal && signal.aborted) throw abortedLoad();
            lastErr = watch.fired ? new Error('source silent for ' + Math.round(silenceMs / 1000) + ' s') : e;
            if (watch.fired) ae(slug, 'ml_model_source_silent', { tried: i, loadedMB: Math.round(loaded / 1048576) });
        } finally {
            watch.stop();
            if (relay) try { signal.removeEventListener('abort', relay); } catch(_){}
        }
    }
    throw new Error('AI music classifier model unavailable: ' + (lastErr && lastErr.message || 'all URLs failed'));
}

// Post-load self-check. Feeds three deliberately-different synthetic inputs
// (zero buffer, Box-Muller gaussian noise, music-like sine+chirp+envelope)
// and asserts the model produces meaningfully-different outputs across
// the three pairs. When the spread is below threshold the model collapsed
// to a constant — that is the WebGPU-fp16 precision-saturation regression
// observed 2026-05-22..24 on SONICS-α. Caller rejects the session and
// falls back to heuristics-only.
//
// Why a 3rd music-like probe matters: telemetry 2026-05-25..28 showed
// real-music windows producing std=0 even on sessions where the 2-input
// noise/zeros sanity check passed (delta ≈ 0.04). Real music's mel
// distribution differs enough from gaussian noise to hit a different
// fp16 saturation regime. The chirp+envelope input is closer to real
// audio's mel signature so a "synthetic OK, real BROKEN" gap closes.
async function runSanityCheck(session, inputName, outputName, onProbe){
    var result = { ok: false, delta: 0, probes: null };
    try {
        var zeros = new Float32Array(WINDOW_SAMPLES);

        // Box-Muller gaussian noise — deterministic via fixed seed.
        var rng = 12345;
        var noise = new Float32Array(WINDOW_SAMPLES);
        for (var i = 0; i < WINDOW_SAMPLES; i += 2) {
            rng = (rng * 1664525 + 1013904223) >>> 0;
            var u1 = (rng / 4294967296) || 1e-9;
            rng = (rng * 1664525 + 1013904223) >>> 0;
            var u2 = (rng / 4294967296);
            var mag = Math.sqrt(-2 * Math.log(u1));
            noise[i]     = mag * Math.cos(2 * Math.PI * u2) * 0.3;
            if (i + 1 < WINDOW_SAMPLES) noise[i + 1] = mag * Math.sin(2 * Math.PI * u2) * 0.3;
        }

        // Music-like probe: A4 tone + linear chirp 220→880 Hz + amplitude
        // envelope. Mel distribution roughly matches real music (peaky low
        // frequencies, smoother high frequencies) so the fp16-saturation
        // regime that catches real audio is exercised here too.
        var music = new Float32Array(WINDOW_SAMPLES);
        var sr = TARGET_SAMPLE_RATE;
        for (var j = 0; j < WINDOW_SAMPLES; j++) {
            var t = j / sr;
            var chirpF = 220 + (880 - 220) * (j / WINDOW_SAMPLES);
            var env = 0.5 + 0.5 * Math.sin(2 * Math.PI * 1.5 * t);  // 1.5 Hz tremolo
            music[j] = env * 0.3 * (Math.sin(2 * Math.PI * 440 * t)
                                     + 0.6 * Math.sin(2 * Math.PI * chirpF * t));
        }

        var probes = [];
        var inputs = [
            { label: 'zeros', buf: zeros },
            { label: 'noise', buf: noise },
            { label: 'music', buf: music }
        ];
        for (var p = 0; p < inputs.length; p++) {
            var feeds = {};
            feeds[inputName] = new ort.Tensor('float32', inputs[p].buf, [1, inputs[p].buf.length]);
            var res = await ortRunSerialized(function(){ return session.run(feeds); });
            var out = res[outputName] || res[Object.keys(res)[0]];
            probes.push({ label: inputs[p].label, pAi: readPredictions(out).pAi });
            if (onProbe) try { onProbe(); } catch(_){}
        }

        // Spread across all 3 probes must be ≥ 0.25.
        //
        // Measured 2026-07-09 on the shipped fp32 model: a healthy session
        // scores zeros=0.943 / noise=0.893 / music=0.323 → delta ≈ 0.664.
        // The fp16-saturated sessions of feedback #507 reported delta ≈ 0.04,
        // i.e. they *passed* the old 0.02 bar and then collapsed to a constant
        // on real music. A 0.25 bar keeps a 2.6x margin under the healthy 0.664
        // while rejecting the 0.04 regime by 6x — so a saturated provider is now
        // caught HERE, at load, before it can produce a single verdict.
        //
        // This is the principled place for saturation detection: it is a direct
        // input-dependence test. The old inference-time spread check could not
        // distinguish "model collapsed" from "model is confident and the track
        // is uniform", and measurably destroyed 48.9 % of correct AI detections
        // (see the non-fatal telemetry-only check in classify()).
        var minP = Math.min(probes[0].pAi, probes[1].pAi, probes[2].pAi);
        var maxP = Math.max(probes[0].pAi, probes[1].pAi, probes[2].pAi);
        var spread = maxP - minP;
        result = { ok: spread >= 0.25, delta: spread, probes: probes };
    } catch (e) {
        result = { ok: false, delta: 0, probes: null, err: String(e && e.message || e) };
    }
    return result;
}

/* A failure of the GPU under the model — not of the model. Measured on /ai-detector
 * since 2026-08-01: «Failed to execute 'createBuffer' on 'GPUDevice': createBuffer
 * failed, size (7680000) is too large» in the middle of a run (desktop and phone),
 * «RangeError: Out of memory» from the WebGPU provider. Only read when the session
 * (or the load) is on WebGPU: on wasm the same words mean the device's memory. */
function gpuFailure(err){
    var m = String((err && err.message) || err || '');
    return /GPUDevice|GPUBuffer|createBuffer|WebGPU|device (?:was )?lost|mapAsync|Out of memory|OperationError/i.test(m);
}

function abortedLoad(){
    var e = new Error('model load abandoned');
    e.name = 'AbortError';
    return e;
}

// ── One ORT run at a time on the PAGE ───────────────────────────────────────
// ORT-Web allows ONE session.run() in flight per runtime instance, and the
// failing second call clears the guard the first one holds — an overlap kills
// BOTH runs ("Session already started" + "Cannot read properties of null
// (reading 'Nd')"). Reproduced here 2026-10-30 on WebGPU (Edge, RTX 4060): two
// classify() calls racing the model load died with exactly that pair, the pair
// production telemetry carried (/ai-detector, Oct 2026: a second file picked
// while the 73 MB model was still loading). The queue is per page, not per
// module: stem-separator.js and mdx-stem-separator.js keep the same 6 lines on
// the same window.__ortRunGate (all three are raw-loadable, none may depend on
// another's load order).
function ortRunSerialized(fn){
    var gate = window.__ortRunGate || (window.__ortRunGate = { tail: Promise.resolve() });
    var out = gate.tail.then(fn, fn);
    gate.tail = out.then(function(){}, function(){});
    return out;
}

async function moveSessionToWasm(slug, cause){
    ae(slug, 'ml_gpu_fallback_wasm', { at: 'run', reason: String((cause && cause.message) || cause).slice(0, 160) });
    var old = onnxSession;
    onnxSession = null; sessionOnGpu = false;
    classifierAvailable = null; modelLoadPromise = null;
    avoidWebGPU = true;
    try { if (old && old.release) await old.release(); } catch(_){}
    return ensureSession(slug, null);
}

async function ensureSession(slug, onProgress){
    if (onnxSession) return onnxSession;
    if (onProgress) loadListeners.push(onProgress);        // every waiter hears the load, not only the first
    if (modelLoadPromise) {
        tellLoad();
        return modelLoadPromise;
    }
    var gen = loadGen;
    var myAbort = (typeof AbortController === 'function') ? new AbortController() : null;
    loadAbort = myAbort;
    function abandoned(){
        if (gen === loadGen) return false;
        throw abortedLoad();
    }
    var attempt = (async function(){
        var t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        enterPhase('ort');
        await loadOrt();
        abandoned();
        enterPhase('fetch');
        var bytes = await fetchModelBytes(slug, tellLoad, myAbort ? myAbort.signal : null);
        abandoned();
        var sizeMB = Math.round(bytes.length / 1048576);   // before create: the proxy worker takes the buffer with it
        // Provider list: WebGPU first (3-5x faster than WASM on transformer
        // graphs), WASM fallback. Both safe with fp32 weights — the 2026-05
        // WebGPU saturation regression (constant pAi=0.81 / "AI 81 %" on
        // legitimate human music, root cause of feedback #507 Lennart) lived
        // in ORT-Web's fp16 kernel implementations, not in the model itself
        // (CPU fp16 vs fp32 verified identical to 1e-4). Now that the model
        // ships fp32, WebGPU and WASM both use their mature fp32 kernel paths
        // and no longer collapse. Defense-in-depth (preload 3-probe sanity +
        // inference-time spread check) stays as a safety net.
        var providers = ['webgpu', 'wasm'];
        enterPhase('adapter');

        /* `ort.env.wasm.proxy` is GLOBAL to the shared `window.ort`, and ORT
         * honours it only for the FIRST session it builds. stem-separator.js
         * sets it (`if (!hasWebGPU) ort.env.wasm.proxy = true`) right before
         * its own create — which worked only while the separator always went
         * first. Since 2026-09-05 the classifier runs first (it reads the full
         * mix and never needed stems), and a main-thread ORT init left the
         * separator asking for a proxy worker that could no longer exist:
         *     stem_preload_failed(worker not ready)
         *     engine_warm_failed(init_returned_false)
         *     stem_split_failed(worker not ready)
         * Reproduced end-to-end; the healthy Deep run lost its stem cards.
         *
         * So whoever is first decides, and both must decide the SAME way. The
         * condition is copied from stem-separator.js on purpose: wasm-only →
         * proxy worker; WebGPU → leave it alone, because ort 1.24's
         * proxy+jsep combination is not battle-tested here.
         *
         * Second effect, wanted: SONICS inference is 12.8 s median and 56.6 s
         * at p90 (90 d, RU shard) and until now ran ON THE MAIN THREAD. */
        var hasWebGPU = false;
        if (!avoidWebGPU && typeof navigator !== 'undefined' && 'gpu' in navigator) {
            /* requestAdapter() can stay pending for good on a broken driver;
             * ORT asks for it again inside create(). An adapter that has not
             * answered in ADAPTER_WAIT_MS is one this page does not wait for
             * twice: the session is asked for on wasm alone. */
            var adapter = null;
            var adapterMs = limit('adapterMs', ADAPTER_WAIT_MS);
            try { adapter = await within(navigator.gpu.requestAdapter(), adapterMs, 'silent'); } catch(_){}
            if (adapter === 'silent') {
                avoidWebGPU = true;
                ae(slug, 'ml_gpu_adapter_silent', { ms: adapterMs });
            } else {
                hasWebGPU = !!adapter;
            }
        }
        abandoned();
        if (avoidWebGPU) providers = ['wasm'];
        loadAskedWebGPU = hasWebGPU;
        if (!hasWebGPU) {
            try { ort.env.wasm.proxy = true; } catch(_){}
        }

        try {
            enterPhase('create');
            var created = await ort.InferenceSession.create(bytes.buffer, {
                executionProviders: providers,
                graphOptimizationLevel: 'all'
            });
            if (gen !== loadGen) {               // forgotten while it was being built
                try { created.release && created.release(); } catch(_){}
                abandoned();
            }
            // Discover IO names so we don't hard-code 'input'/'output'. PyTorch
            // export defaults to these but other export pipelines differ.
            modelInputName  = (created.inputNames  && created.inputNames[0])  || 'input';
            modelOutputName = (created.outputNames && created.outputNames[0]) || 'output';

            // Sanity check: confirm the model output varies with input. Feed
            // two known-distinct inputs (zero buffer + Gaussian-like noise)
            // and assert outputs differ by ≥ 0.01. This catches any future
            // provider/precision combo that collapses to a constant output,
            // which is the WebGPU bug we just walked out of. Cheap: two
            // 1.92M-sample forward passes (~0.5-1.5 s on modern WASM).
            // The session is PUBLISHED (onnxSession) only once it has passed
            // its self-check. It used to be published before the probes, and
            // ensureSession() hands a published session straight to any caller:
            // a second classify() arriving during the probes ran on it while a
            // probe was in flight (the run queue now also forbids the overlap —
            // this keeps a session that may yet be rejected out of anyone's hands).
            enterPhase('sanity');
            var sanity = await runSanityCheck(created, modelInputName, modelOutputName, tellLoad);
            if (gen !== loadGen) {
                try { created.release && created.release(); } catch(_){}
                abandoned();
            }
            if (!sanity.ok) {
                try { created.release && created.release(); } catch(_){}
                if (sanity.err) {
                    /* A probe that THREW is not a model that computed wrong: the
                     * provider gave out under it. Latching it as «collapsed» kept the
                     * classifier off for the rest of the page. */
                    ae(slug, 'ml_classifier_probe_threw', { reason: String(sanity.err).slice(0, 160), provider: hasWebGPU && !avoidWebGPU ? 'webgpu' : 'wasm' });
                    var pe = new Error('classifier probe failed: ' + sanity.err);
                    pe.gpu = hasWebGPU && !avoidWebGPU && gpuFailure(sanity.err);
                    throw pe;
                }
                classifierAvailable = false;
                ae(slug, 'ml_classifier_sanity_failed', { delta: sanity.delta, probes: sanity.probes, provider: providers[0] });
                throw new Error('SONICS classifier failed self-check (output collapsed to constant — provider regression)');
            }
            onnxSession = created;
            sessionOnGpu = hasWebGPU && !avoidWebGPU;

            ae(slug, 'ml_classifier_loaded', {
                ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0),
                sizeMB: sizeMB,
                provider: hasWebGPU ? 'webgpu' : 'wasm',
                input: modelInputName,
                sanity_delta: sanity.delta
            });
            classifierAvailable = true;
            return onnxSession;
        } catch(e) {
            /* The proxy worker is a NEW failure surface for this module (it ran
             * on the main thread until 2026-09-05). The separator can degrade to
             * heuristics when it fails; this classifier cannot — it IS the
             * verdict. So a proxy-mode failure retries once on the main thread
             * rather than costing the only layer with measured discrimination.
             * Not retried for the self-check failure: that is a provider
             * regression the retry would reproduce. */
            var _proxyWasOn = false;
            try { _proxyWasOn = !!(ort && ort.env && ort.env.wasm && ort.env.wasm.proxy); } catch(_){}
            var _selfCheck = /self-check/.test(String((e && e.message) || ''));
            var _collapsed = _selfCheck;
            if (gen !== loadGen) throw e;        // a forgotten attempt does not retry and remembers nothing
            /* The session was built on WebGPU and the GPU failed under its probes:
             * the wasm provider of the SAME runtime is already initialised (the
             * WebGPU build runs on it), so one more session on wasm alone is
             * possible here. (A failure inside the provider's own init — «[wasm]
             * previous call to initWasm() failed» — is not: that runtime is done.) */
            if (e && e.gpu && !avoidWebGPU && !onnxSession) {
                avoidWebGPU = true;
                ae(slug, 'ml_gpu_fallback_wasm', { at: 'load', reason: String((e && e.message) || e).slice(0, 160) });
                try {
                    if (!bytes.buffer.byteLength) bytes = await fetchModelBytes(slug, tellLoad, myAbort ? myAbort.signal : null);
                    var created3 = await ort.InferenceSession.create(bytes.buffer, { executionProviders: ['wasm'], graphOptimizationLevel: 'all' });
                    modelInputName  = (created3.inputNames  && created3.inputNames[0])  || 'input';
                    modelOutputName = (created3.outputNames && created3.outputNames[0]) || 'output';
                    var sanity3 = await runSanityCheck(created3, modelInputName, modelOutputName, tellLoad);
                    if (gen !== loadGen) { try { created3.release && created3.release(); } catch(_){} abandoned(); }
                    if (sanity3.ok) {
                        onnxSession = created3;
                        sessionOnGpu = false;
                        ae(slug, 'ml_classifier_loaded', {
                            ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0),
                            sizeMB: sizeMB, provider: 'wasm-after-gpu', input: modelInputName, sanity_delta: sanity3.delta
                        });
                        classifierAvailable = true;
                        return onnxSession;
                    }
                    try { created3.release && created3.release(); } catch(_){}
                    if (!sanity3.err) _collapsed = true;
                } catch(_e3){ if (gen !== loadGen) throw _e3; }
            }
            if (_proxyWasOn && !_selfCheck && !onnxSession) {
                ae(slug, 'ml_proxy_retry_main_thread', {
                    reason: String((e && e.message) || e).slice(0, 160)
                });
                try { ort.env.wasm.proxy = false; } catch(_){}
                try {
                    /* The proxy worker took the buffer with it (that is why the
                     * size above is read before create): handing the same,
                     * now empty, buffer to the retry made it fail every time.
                     * The bytes come back from the HTTP cache. */
                    if (!bytes.buffer.byteLength) bytes = await fetchModelBytes(slug, tellLoad, myAbort ? myAbort.signal : null);
                    var created2 = await ort.InferenceSession.create(bytes.buffer, {
                        executionProviders: providers,
                        graphOptimizationLevel: 'all'
                    });
                    modelInputName  = (created2.inputNames  && created2.inputNames[0])  || 'input';
                    modelOutputName = (created2.outputNames && created2.outputNames[0]) || 'output';
                    var sanity2 = await runSanityCheck(created2, modelInputName, modelOutputName, tellLoad);
                    if (sanity2.ok) {
                        onnxSession = created2;
                        sessionOnGpu = false;
                        ae(slug, 'ml_classifier_loaded', {
                            ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0),
                            sizeMB: sizeMB,
                            provider: 'wasm-noproxy',
                            input: modelInputName,
                            sanity_delta: sanity2.delta
                        });
                        classifierAvailable = true;
                        return onnxSession;
                    }
                    if (!sanity2.err) _collapsed = true;
                    try { created2.release && created2.release(); } catch(_){}
                } catch(_e2){ onnxSession = null; }
            }
            /* Only a model that computed WRONG here is remembered against the
             * device. Any other failure of session creation (a truncated
             * download, a worker that did not come up, a busy GPU) is one
             * failed attempt: latching it made every later press answer
             * «classifier unavailable» without trying — 8 of 24 failed runs in
             * a week — under a message that says «try again». */
            classifierAvailable = _collapsed ? false : null;
            throw e;
        }
    })();
    modelLoadPromise = attempt;
    /* A rejected attempt is not the answer to the next call. The model fetch
     * and the ORT load sit before the try above, so their failure used to
     * leave this promise cached: every retry got the same «Failed to fetch»
     * back instantly until the page was reloaded. */
    attempt.then(null, function(){ if (modelLoadPromise === attempt) modelLoadPromise = null; });
    /* Settled either way: nobody is waiting on this load any more. */
    var settled = function(){ if (gen === loadGen) { loadListeners = []; loadPhase = ''; } };
    attempt.then(settled, settled);
    return attempt;
}

// Resample-to-16k + downmix-to-mono via OfflineAudioContext. Works on every
// modern browser and is bit-accurate per spec. Returns a single Float32Array
// (length = ceil(sourceDuration * 16000)).
/* ── Deterministic resampling ─────────────────────────────────────────────
 *
 * We used to resample with OfflineAudioContext. Its resampler is
 * implementation-defined, and the classifier turns out to be highly sensitive
 * to the top ~500 Hz of the band. Measured 2026-07-09 on the SAME file, same
 * model, same code:
 *
 *     chromium  pAi 65.8 %      firefox  pAi 13.2 %
 *     h_techno  pAi 78.9 %      firefox  pAi  6.6 %      (max spread 72.3 pts)
 *
 * Root cause: Chromium's resampler preserves energy right up to 8 kHz, Firefox's
 * (like ffmpeg's) rolls it off. Browser vs ffmpeg on one file: waveform corr
 * 0.9951, lag 0, residual only 0.97 % of energy — but ALL of it at 7.5-8 kHz
 * (+1.4 dB vs -5.8 dB at 7.5-7.9 k; -1.7 dB vs -12.8 dB at 7.9-8 k) → 33.2 % vs
 * 14.4 %. So the verdict depended on the user's browser.
 *
 * Fix: own polyphase windowed-sinc resampler. Bake-off, max |chromium-firefox|
 * over 6 tracks:
 *     current OfflineAudioContext ....... 72.30 pts
 *     decode straight into a 16 kHz ctx .. 47.32 pts   (still the engine's resampler)
 *     4x biquad lowpass then resample ....  6.12 pts   (and it destroyed the AI
 *                                                       signal: a borderline Suno
 *                                                       track fell 97.1 -> 8.5)
 *     THIS (own sinc) ....................  0.02 pts   ← deterministic
 *
 * It is also reproducible offline (identical twin in _aidtest/embed.py), which is
 * the precondition for ever refitting the classifier head on our own data.
 *
 * The model is gain-invariant (verified -12 dB … +12 dB → identical pAi), so no
 * loudness normalisation is needed or wanted here.
 * ───────────────────────────────────────────────────────────────────────── */
/* Kernel geometry. Do NOT raise RS_CUTOFF "to keep more signal" — measured.
 *
 * The disputed 7.6-8 kHz band carries BOTH real classifier signal AND a codec /
 * resampler artefact (that band's energy alone separates our human vs AI sets
 * with AUC 0.69). Keeping more of it lifts recall and false positives together;
 * there is no free lunch. Full 368-track calibration, red threshold = 98:
 *
 *                       recall @ 0 false accusations   amber FP@70   AUC
 *     16 / 0.95  (this)            51.1 %                  2.6 %    0.9025
 *     24 / 0.99                    53.2 %                  6.2 %    0.9121
 *
 * +2.1 pts of Suno recall costs 2.4x more humans being told "possibly AI" —
 * which is exactly the complaint (#952) this whole line of work started from.
 * A wrongly-amber human complains; an undetected Suno track does not. 16/0.95
 * also uses half the taps, so it is cheaper in the browser. */
var RS_HALF_WIDTH = 16;     // sinc zero-crossings each side of centre
var RS_PHASES     = 256;    // sub-sample phase quantisation (<= 1/512 sample error)
var RS_CUTOFF     = 0.95;   // fraction of the output Nyquist kept

var rsCache = null;         // {ratio, taps, halfTaps, table, norm}

function buildSincTable(ratio){
    if (rsCache && rsCache.ratio === ratio) return rsCache;
    var A2   = Math.max(1, Math.round(RS_HALF_WIDTH * Math.max(1, ratio)));
    var taps = 2 * A2;
    var fc   = 0.5 * Math.min(1, 1 / ratio) * RS_CUTOFF;   // normalised to the INPUT rate
    var table = new Float64Array(RS_PHASES * taps);
    var norm  = new Float64Array(RS_PHASES);
    for (var p = 0; p < RS_PHASES; p++) {
        var frac = p / RS_PHASES, sum = 0;
        for (var t = 0; t < taps; t++) {
            var x = (t - A2 + 1) - frac;                    // distance from centre, in input samples
            var u = 2 * fc * x;
            var s = (Math.abs(u) < 1e-9) ? 1 : Math.sin(Math.PI * u) / (Math.PI * u);
            var w = 0;
            if (x >= -A2 && x <= A2) {
                var v = (x + A2) / (2 * A2);
                w = 0.42 - 0.5 * Math.cos(2 * Math.PI * v) + 0.08 * Math.cos(4 * Math.PI * v);
            }
            var k = s * w;
            table[p * taps + t] = k;
            sum += k;
        }
        norm[p] = sum || 1;
    }
    rsCache = { ratio: ratio, taps: taps, halfTaps: A2, table: table, norm: norm };
    return rsCache;
}

function resampleTo16k(input, srIn){
    if (srIn === TARGET_SAMPLE_RATE) return input.slice ? input.slice() : new Float32Array(input);
    var ratio = srIn / TARGET_SAMPLE_RATE;
    var K = buildSincTable(ratio);
    var taps = K.taps, A2 = K.halfTaps, table = K.table, norm = K.norm;
    var outLen = Math.floor(input.length / ratio);
    var out = new Float32Array(outLen);
    var n = input.length;
    for (var i = 0; i < outLen; i++) {
        var center = i * ratio;
        var base = Math.floor(center);
        var p = Math.round((center - base) * RS_PHASES);
        if (p >= RS_PHASES) { p -= RS_PHASES; base += 1; }
        var off = p * taps, start = base - A2 + 1, acc = 0;
        for (var t = 0; t < taps; t++) {
            var idx = start + t;
            if (idx >= 0 && idx < n) acc += input[idx] * table[off + t];
        }
        out[i] = acc / norm[p];
    }
    return out;
}

async function toMono16k(audioBuffer){
    var ch = audioBuffer.numberOfChannels;
    if (audioBuffer.sampleRate === TARGET_SAMPLE_RATE && ch === 1) {
        return audioBuffer.getChannelData(0).slice();
    }
    // Deterministic downmix: plain average of all channels.
    var n = audioBuffer.length;
    var mono;
    if (ch === 1) {
        mono = audioBuffer.getChannelData(0);
    } else {
        mono = new Float32Array(n);
        for (var c = 0; c < ch; c++) {
            var d = audioBuffer.getChannelData(c);
            for (var i = 0; i < n; i++) mono[i] += d[i];
        }
        for (var i2 = 0; i2 < n; i2++) mono[i2] /= ch;
    }
    return resampleTo16k(mono, audioBuffer.sampleRate);
}

/* Fill `n` samples by mirroring `src` back and forth (forward, reversed,
   forward, …). Used instead of zero-padding short tracks.

   The model REQUIRES exactly WINDOW_SAMPLES (verified: a 60 s tensor throws
   "Got invalid dim"), so some padding is unavoidable — but zeros are the worst
   possible filler here: the classifier scores pure silence at pAi = 0.943 and
   noise at 0.893, so every appended second of silence drags a short clip toward
   "AI". Measured 2026-07-09 on 20 human clips ≤ 120 s: corr(padding %, pAi) =
   +0.67, false positives 45 % vs 18.8 % for full-length tracks — a 30 s human
   house track scored 95.6 % AI.

   Bake-off on those clips (human FP @70 / AI recall @70):
       zero-pad  45.0 % / 100 %   ← was production
       tile      30.0 % / 100 %
       reflect   25.0 % / 100 %   ← chosen
       centred   30.0 % / 100 %
   Reflect also collapses the artefact's fingerprint: corr(padding %, pAi) falls
   0.67 → 0.09, and mean pAi of short human clips lands at 33.5, matching the
   36.0 of unpadded full-length human tracks. No AI detection was lost.
   Mirroring (rather than looping) avoids a waveform discontinuity at the seam. */
function reflectFill(src, n){
    var out = new Float32Array(n);
    var L = src.length;
    if (!L) return out;
    for (var i = 0; i < n; i++) {
        var j = i % L;
        out[i] = (Math.floor(i / L) % 2) ? src[L - 1 - j] : src[j];
    }
    return out;
}

function buildWindows(samples){
    // Track shorter than one window → reflect-pad to WINDOW_SAMPLES, single window.
    if (samples.length <= WINDOW_SAMPLES) {
        return [reflectFill(samples, WINDOW_SAMPLES)];
    }
    // Track longer than one window → 50 % overlap stride, capped at N_WINDOWS_MAX.
    var windows = [];
    for (var off = 0; off + WINDOW_SAMPLES <= samples.length && windows.length < N_WINDOWS_MAX; off += WINDOW_STRIDE_SAMP) {
        windows.push(samples.subarray(off, off + WINDOW_SAMPLES));
    }
    // Always include the trailing window so the last seconds of long tracks
    // aren't dropped — pad-to-WINDOW_SAMPLES if it's a partial.
    if (windows.length < N_WINDOWS_MAX) {
        var lastStart = Math.max(0, samples.length - WINDOW_SAMPLES);
        var existsLast = windows.length && (windows[windows.length - 1].byteOffset === samples.byteOffset + lastStart * 4);
        if (!existsLast) {
            if (lastStart + WINDOW_SAMPLES <= samples.length) {
                windows.push(samples.subarray(lastStart, lastStart + WINDOW_SAMPLES));
            } else {
                // Defensive: unreachable while lastStart === len - WINDOW_SAMPLES,
                // but if it ever runs, reflect-fill rather than pad with silence
                // (the model reads silence as pAi ≈ 0.94).
                windows.push(reflectFill(samples.subarray(lastStart), WINDOW_SAMPLES));
            }
        }
    }
    return windows;
}

function sigmoid(x){
    // Numerically stable for both signs.
    if (x >= 0) {
        var z = Math.exp(-x);
        return 1 / (1 + z);
    } else {
        var ez = Math.exp(x);
        return ez / (1 + ez);
    }
}

// Class-label vocabulary the model outputs. NULL = binary head (current).
// When the multiclass model from Phase F.1 ships, fill in the labels in the
// SAME order as the model's softmax outputs (typically: human first, then
// generators alphabetically) — no other code change required, the UI in
// ai-detector.blade.php already renders `result.topPredictions[]` as
// top-K colored chips.
//
// Example for the SpecTTTra-γ-multiclass-7 we plan to train on FakeMusicCaps
// + SONICS:  ['human', 'suno', 'udio', 'musicgen', 'audioldm',
//             'stable_audio', 'mustango']
var MODEL_CLASS_LABELS = null;

// Parse an ORT output tensor into a forward-compatible predictions structure.
// Returns `{topPredictions: [{label, conf}, …], pAi}` where:
//   - For binary heads (current SONICS): topPredictions has 2 entries
//     [{label:'AI', conf:pAi}, {label:'Human', conf:1-pAi}].
//   - For multiclass heads (future Phase F.1): softmax over MODEL_CLASS_LABELS,
//     sorted by confidence descending, with `pAi = 1 - conf[human]` (or 1.0
//     if 'human' isn't in the label vocab).
// Shape handling:
//   - [1, 1] raw logit → sigmoid → binary
//   - [1]    raw logit → sigmoid → binary
//   - [1, 1] in [0,1]  → as-is   → binary
//   - [1, 2] softmax or logits   → binary (index 1 = "fake")
//   - [1, N] N≥3       → multiclass (softmax, labels from MODEL_CLASS_LABELS)
function readPredictions(tensor){
    var data = tensor.data;
    var n = data.length;

    // --- Binary heads (legacy SONICS) ---
    if (n === 1) {
        var v = data[0];
        /* The old rule was `(v >= 0 && v <= 1) ? v : sigmoid(v)` — "if it looks
         * like a probability, take it as one". For a graph whose single output is
         * a LOGIT that is silently wrong over the whole band [0,1]:
         *     logit  0.00 → reported 0 % AI, true 50.0 %
         *     logit -0.01 → 49.75 %   but   logit +0.01 → 1 %   (a cliff at zero)
         *     logit  1.00 → reported 100 %, true 73.1 %
         * Our exported graph names its output "logit", so decide explicitly rather
         * than guess from the value. (Measured: 0/35 windows landed in [0,1] before
         * the deterministic-resampler change, but the band is reachable — human
         * scores now sit lower, i.e. closer to it.) */
        var outIsLogit = typeof modelOutputName === 'string' && /logit/i.test(modelOutputName);
        var p = outIsLogit ? sigmoid(v) : ((v >= 0 && v <= 1) ? v : sigmoid(v));
        return binaryPredictions(p);
    }
    if (n === 2) {
        var a = data[0], b = data[1];
        var pAi;
        if (a >= 0 && a <= 1 && b >= 0 && b <= 1 && Math.abs((a + b) - 1) < 0.05) {
            pAi = b;
        } else {
            var maxAB = Math.max(a, b);
            var ea = Math.exp(a - maxAB), eb = Math.exp(b - maxAB);
            pAi = eb / (ea + eb);
        }
        return binaryPredictions(pAi);
    }

    // --- Multiclass head (Phase F.1+) ---
    // Softmax over N logits. If we have labels, use them; otherwise generic
    // 'class_0', 'class_1', … so the structure is still usable.
    var maxL = -Infinity;
    for (var i = 0; i < n; i++) if (data[i] > maxL) maxL = data[i];
    var expSum = 0;
    var probs = new Array(n);
    for (var i = 0; i < n; i++) { probs[i] = Math.exp(data[i] - maxL); expSum += probs[i]; }
    for (var i = 0; i < n; i++) probs[i] /= expSum;

    var labels = (Array.isArray(MODEL_CLASS_LABELS) && MODEL_CLASS_LABELS.length === n)
        ? MODEL_CLASS_LABELS
        : null;
    var top = [];
    for (var i = 0; i < n; i++) {
        top.push({ label: labels ? labels[i] : ('class_' + i), conf: probs[i] });
    }
    top.sort(function(x, y){ return y.conf - x.conf; });

    // pAi = 1 - P(human). If 'human' isn't in the vocab, assume the model is
    // already AI-only and treat the whole distribution as AI generators.
    var humanIdx = labels ? labels.indexOf('human') : -1;
    var pAi = humanIdx >= 0 ? (1 - probs[humanIdx]) : 1.0;

    return { pAi: pAi, topPredictions: top };
}

function binaryPredictions(pAi){
    return {
        pAi: pAi,
        topPredictions: [
            { label: 'AI',    conf: pAi },
            { label: 'human', conf: 1 - pAi }
        ]
    };
}

// Aggregate per-window predictions into one per-track prediction by
// confidence-averaging each label across windows. Falls back to mean(pAi)
// when topPredictions arrays disagree in shape (e.g. mid-classify model swap).
function aggregatePredictions(perWindowPreds){
    if (!perWindowPreds.length) return { pAi: 0, topPredictions: [] };
    var first = perWindowPreds[0];
    var n = first.topPredictions.length;
    var sums = {};      // label → sum-of-conf across windows
    var totalPAi = 0;
    for (var i = 0; i < perWindowPreds.length; i++) {
        var pred = perWindowPreds[i];
        totalPAi += pred.pAi;
        for (var k = 0; k < pred.topPredictions.length; k++) {
            var t = pred.topPredictions[k];
            sums[t.label] = (sums[t.label] || 0) + t.conf;
        }
    }
    var W = perWindowPreds.length;
    var meanPAi = totalPAi / W;
    var labelKeys = Object.keys(sums);
    var aggTop = labelKeys.map(function(L){ return { label: L, conf: sums[L] / W }; });
    aggTop.sort(function(a, b){ return b.conf - a.conf; });
    return { pAi: meanPAi, topPredictions: aggTop };
}

function checkAborted(signal){
    if (signal && signal.aborted) {
        var e = new Error('aborted');
        e.name = 'AbortError';
        throw e;
    }
}

window.AiMusicClassifier = {
    __isStub: false,
    modelVersion: MODEL_VERSION,

    isReady: function(){
        return classifierAvailable === true && !!onnxSession;
    },

    isUnavailable: function(){
        return classifierAvailable === false;
    },

    /**
     * The 16 kHz mono signal the model hears — plain channel average through this
     * module's deterministic resampler. Needs no model: the synthesis fingerprint
     * (ai-music-fingerprint.js) reads the same samples, and classify() takes them
     * back as opts.samples16k instead of resampling the file a second time.
     * Resolves to a Float32Array (the resampler yields to the page between blocks).
     */
    mono16k: function(audioBuffer){
        return toMono16k(audioBuffer);
    },

    /** Where the load in flight is: ort | fetch | adapter | create | sanity; '' when none. */
    loadPhase: function(){
        return modelLoadPromise ? loadPhase : '';
    },

    /**
     * Forget the load in flight — for the page's stall watchdog. The attempt
     * cannot be cancelled, but while it stayed remembered every retry waited
     * on the same silent promise. Returns the phase it was in; if that was a
     * GPU phase of an attempt that had asked for WebGPU, the next attempt
     * asks for wasm alone — the same request would hang the same way.
     */
    abandonLoad: function(slug){
        if (!modelLoadPromise || onnxSession) return '';
        var was = loadPhase;
        loadGen++;
        if (loadAbort) { try { loadAbort.abort(); } catch(_){} loadAbort = null; }
        modelLoadPromise = null;
        loadListeners = [];
        loadPhase = '';
        if (!window.ort) ortLoadPromise = null;          // the script tag itself never answered
        if (loadAskedWebGPU && (was === 'create' || was === 'sanity')) avoidWebGPU = true;
        ae(slug, 'ml_load_abandoned', { phase: was, webgpu: loadAskedWebGPU ? 1 : 0, next: avoidWebGPU ? 'wasm' : 'same' });
        return was;
    },

    /**
     * Lazy-load ORT + SONICS ONNX. EngineWarmer-compatible: returns
     * Promise<boolean>. Re-callable; cached after first success.
     */
    preload: function(slug, onProgress){
        if (classifierAvailable === true) {
            if (onProgress) try { onProgress(1); } catch(_){}
            return Promise.resolve(true);
        }
        return ensureSession(slug, onProgress).then(function(){ return true; });
    },

    /**
     * Run inference on an AudioBuffer (any rate / channel count). Resamples
     * to 16 kHz mono internally. Resolves to {pAi, perWindow, ...}.
     *
     * Designed to never *throw on the happy path* if preload succeeded —
     * a single window inference failure aborts the whole classify() so the
     * caller can fall back. AbortSignal aborts mid-loop with AbortError.
     */
    classify: async function(audioBuffer, opts){
        opts = opts || {};
        var slug = opts.slug || 'ai-detector';
        var signal = opts.signal || null;
        var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;

        checkAborted(signal);
        await ensureSession(slug, opts.onLoadProgress || null);
        checkAborted(signal);

        var t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        var samples = (opts.samples16k && opts.samples16k.length) ? opts.samples16k : await toMono16k(audioBuffer);
        var windows = buildWindows(samples);
        var perWindow = new Array(windows.length);             // legacy: pAi numeric per window
        var perWindowPredictions = new Array(windows.length);  // multiclass-forward: {pAi, topPredictions} per window
        var inferStart = (typeof performance !== 'undefined' ? performance.now() : Date.now());

        var movedToWasm = false;
        for (var w = 0; w < windows.length; w++) {
            checkAborted(signal);
            // ORT requires its own copy of the buffer (transferred to wasm
            // heap). Slice to detach from the AudioBuffer-backed view.
            var slice = windows[w].slice ? windows[w].slice() : new Float32Array(windows[w]);
            var feeds = {};
            feeds[modelInputName] = new ort.Tensor('float32', slice, [1, slice.length]);
            var results;
            try {
                results = await ortRunSerialized(function(){ checkAborted(signal); return onnxSession.run(feeds); });
            } catch (runErr) {
                /* The GPU gave out under a window (createBuffer refused, device lost,
                 * out of memory): the model is fine and the wasm provider of this
                 * runtime is up — move the session there once and repeat the window,
                 * instead of failing a run the person already waited for. */
                if (movedToWasm || !sessionOnGpu || !gpuFailure(runErr) || (signal && signal.aborted)) throw runErr;
                movedToWasm = true;
                await moveSessionToWasm(slug, runErr);
                checkAborted(signal);
                w--;
                continue;
            }
            var out = results[modelOutputName] || results[Object.keys(results)[0]];
            var pred = readPredictions(out);
            perWindow[w] = pred.pAi;
            perWindowPredictions[w] = pred;
            if (onProgress) try { onProgress(w + 1, windows.length); } catch(_){}
        }

        var sum = 0;
        for (var i = 0; i < perWindow.length; i++) sum += perWindow[i];
        var mean = perWindow.length ? sum / perWindow.length : 0;

        // Low-spread observability — NON-FATAL by design.
        //
        // A near-zero spread across windows was previously treated as proof of
        // precision saturation and the whole ML result was thrown away. Measured
        // 2026-07-09 on a 368-track labelled set (227 human CC pre-2022 + 141
        // Suno), that check was catastrophically wrong:
        //
        //   * it fired on 48.9 % of AI tracks (69/141) and 2.6 % of human ones;
        //   * the discarded AI tracks scored a mean pAi of 0.987 — they are the
        //     model's BEST answers. Suno is simply consistent across windows;
        //   * the discarded human tracks recovered 0.029 / 0.035 / 0.068 / 0.102
        //     / 0.179 — confidently, correctly human;
        //   * effective AI recall was 39.7 % with the throw, 88.7 % without it,
        //     at an unchanged human false-positive rate (20.7 % -> 21.1 %).
        //
        // Low spread means "the model agreed with itself", which a *confident*
        // model does on uniform material. It is not distinguishable from
        // saturation by this statistic, so this is the wrong place to judge.
        // Saturation is an INPUT-DEPENDENCE failure and is now caught up front
        // by runSanityCheck()'s three-probe test, whose bar was raised 0.02 ->
        // 0.25 (healthy model measures 0.664; the #507 regression measured 0.04).
        //
        // We still emit the event so a future provider regression is visible at
        // population level in telemetry — but we never again discard a verdict
        // for being confident.
        if (perWindow.length >= 2) {
            var minPAi = Infinity, maxPAi = -Infinity;
            for (var k = 0; k < perWindow.length; k++) {
                if (perWindow[k] < minPAi) minPAi = perWindow[k];
                if (perWindow[k] > maxPAi) maxPAi = perWindow[k];
            }
            var spread = maxPAi - minPAi;
            if (spread < 0.005) {
                ae(slug, 'ml_inference_low_spread', {
                    chunks: perWindow.length,
                    mean: Math.round(mean * 100),
                    spread: Math.round(spread * 10000) / 10000
                });
            }
        }

        var aggregate = aggregatePredictions(perWindowPredictions);

        var result = {
            pAi: mean,                                     // legacy scalar — keep working
            topPredictions: aggregate.topPredictions,      // [{label, conf}, …] sorted desc — Phase F.1-ready
            perWindow: perWindow,                          // legacy per-window pAi numbers
            perWindowPredictions: perWindowPredictions,    // per-window full prediction objects
            windowsCount: windows.length,
            durationCovered: Math.min(audioBuffer.duration,
                                      (windows.length === 1)
                                          ? Math.min(WINDOW_DURATION_S, audioBuffer.duration)
                                          : (WINDOW_DURATION_S + (windows.length - 1) * WINDOW_STRIDE_S)),
            inferenceMs: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - inferStart),
            modelVersion: MODEL_VERSION,
            isMulticlass: Array.isArray(MODEL_CLASS_LABELS) && MODEL_CLASS_LABELS.length > 2
        };

        ae(slug, 'ml_inference_done', {
            ms: result.inferenceMs,
            model: MODEL_VERSION,          // segment telemetry by head (stock vs -mg1 etc.)
            chunks: result.windowsCount,
            mean: Math.round(mean * 100),
            std: (function(){
                if (perWindow.length < 2) return 0;
                var v = 0;
                for (var i = 0; i < perWindow.length; i++) v += (perWindow[i] - mean) * (perWindow[i] - mean);
                return Math.round(Math.sqrt(v / perWindow.length) * 100);
            })(),
            top1: result.topPredictions[0] ? result.topPredictions[0].label : null,
            top1_conf: result.topPredictions[0] ? Math.round(result.topPredictions[0].conf * 100) : null,
            n_classes: result.topPredictions.length
        });

        // Help the GC — long Float32Arrays held by the ORT closure can
        // trip V8 into keeping ~40 MB live across calls.
        samples = null; windows = null;
        return result;
    },

    // Test-only: clear all module state. Production never calls this.
    _reset: function(){
        ortLoadPromise = null;
        modelLoadPromise = null;
        loadListeners = []; loadPhase = ''; loadRatio = 0; loadGen++; loadAskedWebGPU = false; avoidWebGPU = false; sessionOnGpu = false;
        onnxSession = null;
        modelInputName = null;
        modelOutputName = null;
        classifierAvailable = null;
    }
};

if (typeof window.__drainSharedModule === 'function') {
    try { window.__drainSharedModule('AiMusicClassifier'); } catch(_){}
}
})();
